import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';
import {add, deleteRecord, fetch, update} from '../services/CustomerSubscriptionsService';
import {
    buildCreateCustomerSubscriptionInput,
    buildUpdateCustomerSubscriptionInput
} from '../services/buildCustomerSubscriptionInput';
import {CustomerSubscription} from '../types';
import {notifySuccess, notifyError} from '../../../components/ServiceNotifications';
import {customerSubscriptionsQueryKeys} from './customerSubscriptionsQueryKeys';

export const useCustomerSubscription = (customerSubscriptionId?: string) => {
    const queryClient = useQueryClient();

    const customerSubscriptionQuery = useQuery({
        queryKey: customerSubscriptionsQueryKeys.detail(customerSubscriptionId),
        queryFn: () => fetch(customerSubscriptionId!),
        enabled: !!customerSubscriptionId,
        staleTime: 30_000,
    });

    // ['customer-subscriptions'] is the root of the list AND the detail keys, so one
    // invalidation reaches both (§8).
    const invalidate = () => queryClient.invalidateQueries({queryKey: customerSubscriptionsQueryKeys.all});

    const createMutation = useMutation({
        mutationFn: (values: CustomerSubscription) => add(buildCreateCustomerSubscriptionInput(values)),
        onSuccess: () => {
            notifySuccess(I18n.get('Customer Subscription added'));
            invalidate();
        },
        onError: notifyError
    });

    const updateMutation = useMutation({
        mutationFn: (values: CustomerSubscription) => {
            if (!customerSubscriptionId) throw new Error('customerSubscriptionId is required parameter');
            return update(buildUpdateCustomerSubscriptionInput(customerSubscriptionId, values));
        },
        onSuccess: () => {
            notifySuccess(I18n.get('Customer Subscription updated'));
            invalidate();
        },
        onError: notifyError
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!customerSubscriptionId) throw new Error('customerSubscriptionId is required parameter');
            return deleteRecord(customerSubscriptionId);
        },
        onSuccess: () => {
            notifySuccess(I18n.get('Customer Subscription deleted'));
            invalidate();
        },
        onError: notifyError
    });

    // Resolve true once the save lands and false when it is rejected, never throw: the
    // drawer awaits this and closes only on true, so a server-side rejection keeps the
    // operator's input on screen (§6, §7). The mutation's onError has already toasted.
    const settle = (run: Promise<unknown>) => run.then(() => true, () => false);

    return {
        record: customerSubscriptionQuery.data,
        loading: customerSubscriptionQuery.isLoading,
        fetchRecord: customerSubscriptionQuery.refetch,
        createRecord: (values: CustomerSubscription) => settle(createMutation.mutateAsync(values)),
        updateRecord: (values: CustomerSubscription) => settle(updateMutation.mutateAsync(values)),
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
